import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useGameStore } from './store/gameStore';
import { useAuthStore } from './store/authStore';
import Game from './components/Game';
import LessonSelector from './components/LessonSelector';
import LoginPage from './components/LoginPage';
import MySentences from './components/MySentences';
import { lessonsApi } from './api/lessons';

const navBtnStyle = (active) => ({
  padding: '8px 16px',
  background: active ? '#6366f1' : 'transparent',
  border: active ? 'none' : '1px solid #334155',
  borderRadius: '8px',
  color: '#e2e8f0',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: 600,
});

const App = () => {
  const [view, setView] = useState('selector'); // 'selector' | 'game' | 'sentences' | 'login'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { setLessons, lessons, hydrateServerPreferences } = useGameStore();
  const { isAuthenticated, user, logout, sessionExpired } = useAuthStore();

  // Pull tile-display preferences from the server whenever the user logs in
  // or the app boots with a valid session.
  useEffect(() => {
    if (isAuthenticated) hydrateServerPreferences();
  }, [isAuthenticated, hydrateServerPreferences]);
  
  // Handle session expiry from API client
  useEffect(() => {
    const handler = () => {
      sessionExpired();
      setView('login');
    };
    window.addEventListener('sb-auth-expired', handler);
    return () => window.removeEventListener('sb-auth-expired', handler);
  }, [sessionExpired]);
  
  // Load lessons metadata from the API (full content fetched lazily per-lesson)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lessonList = await lessonsApi.list();
        if (cancelled) return;
        // Normalise: API returns `audioPath`; the game expects `audio`
        const normalised = lessonList.map((l) => ({
          ...l,
          audio: l.audio ?? l.audioPath ?? null,
        }));
        setLessons(normalised);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load lessons:', err);
        setError(err.message || 'Failed to load lesson data');
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [setLessons]);
  
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#0f172a',
        color: '#94a3b8'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid #334155',
            borderTopColor: '#6366f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p>Loading lessons...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#0f172a',
        color: '#ef4444'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Error</h2>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              background: '#6366f1',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  // Login view (full screen, no nav)
  if (view === 'login') {
    return (
      <LoginPage
        onSuccess={() => setView('sentences')}
        onCancel={() => setView('selector')}
      />
    );
  }
  
  const handleSentencesClick = () => {
    setView(isAuthenticated ? 'sentences' : 'login');
  };
  
  return (
    <DndProvider backend={HTML5Backend}>
      <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif', background: '#0f172a', minHeight: '100vh' }}>
        {/* Nav bar */}
        <nav style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px',
          background: '#1e293b',
          borderBottom: '1px solid #334155',
          flexWrap: 'wrap',
          gap: '8px',
        }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={() => setView('selector')} style={navBtnStyle(view === 'selector')}>
              📚 Lessons
            </button>
            <button onClick={() => setView('game')} style={navBtnStyle(view === 'game')}>
              🧩 Game
            </button>
            <button onClick={handleSentencesClick} style={navBtnStyle(view === 'sentences')}>
              ✏️ My Sentences
            </button>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {isAuthenticated ? (
              <>
                <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                  👤 {user?.username || user?.email}
                  {(user?.role === 'admin' || user?.role === 'superadmin') && (
                    <span style={{ color: '#f59e0b', marginLeft: '6px' }}>(admin)</span>
                  )}
                </span>
                <button
                  onClick={() => { logout(); setView('selector'); }}
                  style={navBtnStyle(false)}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button onClick={() => setView('login')} style={navBtnStyle(false)}>
                🔑 Sign In
              </button>
            )}
          </div>
        </nav>
        
        {/* Views */}
        {view === 'selector' && <LessonSelector onSelect={() => setView('game')} />}
        {view === 'game' && <Game />}
        {view === 'sentences' && (
          isAuthenticated
            ? <MySentences onPractice={() => setView('game')} />
            : <LoginPage onSuccess={() => setView('sentences')} onCancel={() => setView('selector')} />
        )}
      </div>
    </DndProvider>
  );
};

// Router version for production with auth
export const AppWithRouter = () => (
  <Router>
    <DndProvider backend={HTML5Backend}>
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/game" element={<Game />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </DndProvider>
  </Router>
);

export default App;
